import { trpc } from '@tencent/tpro_buglyqq_resource_manager_resource';
import { get } from 'lodash-es';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { getProductId } from '@/infrastructure/product-info';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';

export type IResourcePackage = WithTypeCast<trpc.buglyqq.resource_manager.IResourcePackage>;
export type IBuyResourceReq = WithTypeCast<trpc.buglyqq.resource_manager.IBuyResourceReq>;
export type IGetOwnedResourceListReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetOwnedResourceListReq>;
export type IGetProductExceedReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductExceedReq>;
export type IGetResourceUsageDetailReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetResourceUsageDetailReq>;
export type OverdueDataRecoveryBannerReq = WithTypeCast<trpc.buglyqq.resource_manager.IOverdueDataRecoveryBannerReq>;
export type OverdueDataRecoveryBannerRsp = WithTypeCast<trpc.buglyqq.resource_manager.IOverdueDataRecoveryBannerRsp>;
export type IResourcePackageDetail = WithTypeCast<trpc.buglyqq.resource_manager.IResourcePackageDetail>;
export type IGetProductUsageDetailReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductUsageDetailReq>;
export type IGetProductUsageDetailRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductUsageDetailRsp>;
export type IGetProductExceedRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductExceedRsp>;
export type IGetResourcePriceReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetResourcePriceReq>;
export type IGetResourcePriceRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetResourcePriceRsp>;
export type IGetOwnedResourceListRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetOwnedResourceListRsp>;
export type IBindingResourceToProductReq = WithTypeCast<trpc.buglyqq.resource_manager.IBindingResourceToProductReq>;
export type IBindingResourceToProductRsp = WithTypeCast<trpc.buglyqq.resource_manager.IBindingResourceToProductRsp>;
// eslint-disable-next-line max-len
export type IGetResourceUsageDetailByProductsReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetResourceUsageDetailByProductsReq>;
// eslint-disable-next-line max-len
export type IGetResourceUsageDetailByProductsRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetResourceUsageDetailByProductsRsp>;
export type IGetProductsFromResourceReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductsFromResourceReq>;
export type IGetProductsFromResourceRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetProductsFromResourceRsp>;
export type IResourceProducts = WithTypeCast<trpc.buglyqq.resource_manager.IResourceProducts>;
export type IHasBindingAnnualResourceReq = WithTypeCast<trpc.buglyqq.resource_manager.IHasBindingAnnualResourceReq>;
export type IHasBindingAnnualResourceRsp = WithTypeCast<trpc.buglyqq.resource_manager.IHasBindingAnnualResourceRsp>;
export type IGetMAUResourceUsageReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetMAUResourceUsageReq>;
export type IGetMAUResourceUsageRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetMAUResourceUsageRsp>;
export type IReportCountDetail = WithTypeCast<trpc.buglyqq.resource_manager.IReportCountDetail>;
export type IGetCreditPackageDetailReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditPackageDetailReq>;
export type IGetCreditPackageDetailRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditPackageDetailRsp>;
export type IGetCreditUsageTrendReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditUsageTrendReq>;
export type IGetCreditUsageTrendRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditUsageTrendRsp>;
export type ICreditUsageTrendPoint = WithTypeCast<trpc.buglyqq.resource_manager.ICreditUsageTrendPoint>;
export type IGetMemberUsageSummaryReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetMemberUsageSummaryReq>;
export type IGetMemberUsageSummaryRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetMemberUsageSummaryRsp>;
export type IMemberUsageSummaryItem = WithTypeCast<trpc.buglyqq.resource_manager.IMemberUsageSummaryItem>;
export type IGetCreditUsageDetailReq = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditUsageDetailReq>;
export type IGetCreditUsageDetailRsp = WithTypeCast<trpc.buglyqq.resource_manager.IGetCreditUsageDetailRsp>;
export type ICreditUsageDetailItem = WithTypeCast<trpc.buglyqq.resource_manager.ICreditUsageDetailItem>;

/**
 * 资源包
 */
export class ResourcePackageService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.ResourcePackageService,
    });
  }

  /**
   * 获取可以购买的资源包列表
   */
  public async getBuyableResourceList(): Promise<IResourcePackage[]> {
    try {
      const rsp = await this.trpcPost('GetBuyableResourceList');
      return get(rsp, 'packages', []);
    } catch (e) {
      return [];
    }
  }

  /**
   * 计算资源包组合价格
   */
  public async getResourcePrice(req: IGetResourcePriceReq): Promise<IGetResourcePriceRsp | null> {
    try {
      return await this.trpcPost('GetResourcePrice', req);
    } catch (e) {
      return null;
    }
  }

  /**
   * 购买资源包，生成订单ID
   */
  public async buyResource(req: IBuyResourceReq): Promise<string | null> {
    try {
      const rsp = await this.trpcPost('BuyResource', req);
      return get(rsp, 'orderId', null);
    } catch (e) {
      return null;
    }
  }

  /**
   * 查询已有资源包(个人维度)
   */
  public async getOwnedResourceList(req: IGetOwnedResourceListReq): Promise<IGetOwnedResourceListRsp | null> {
    try {
      const rsp = await this.trpcPost('GetOwnedResourceList', req);
      return {
        resources: get(rsp, 'resources', []),
        total: get(rsp, 'total', 0),
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * 查询已有资源包(产品维度)
   */
  public async getProductResourceList(
    req: IGetOwnedResourceListReq,
    config?: any,
  ): Promise<IGetOwnedResourceListRsp | null> {
    try {
      const rsp = await this.trpcPost('GetProductResourceList', req, config);
      return {
        resources: get(rsp, 'resources', []),
        total: get(rsp, 'total', 0),
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * 查询资源包按天使用量
   */
  public async getResourceUsageDetail(req: IGetResourceUsageDetailReq):
  Promise<IResourcePackageDetail[]> {
    try {
      const rsp = await this.trpcPost('GetResourceUsageDetail', req);
      return get(rsp, 'usageDetail', []);
    } catch (e) {
      return [];
    }
  }

  /**
   * 查看产品的抵扣明细（监控项维度）
   */
  public async getProductUsageDetail(req: IGetProductUsageDetailReq): Promise<IGetProductUsageDetailRsp | null> {
    try {
      return await this.trpcPost('GetProductUsageDetail', req, {
        headers: {
          'X-ProductID': req.productId,
        },
      });
    } catch (e) {
      return null;
    }
  }

  /**
   * 获取当前产品的用量状态(超额欠费)
   */
  public async getProductExceed(req: IGetProductExceedReq): Promise<IGetProductExceedRsp | {}> {
    try {
      return await this.trpcPost('GetProductExceed', req, { headers: {
        'X-ProductID': getProductId(),
      } });
    } catch (e) {
      return {};
    }
  }

  /**
   * 欠费数据恢复banner条
   */
  public async getOverdueDataRecoveryBanner(req: OverdueDataRecoveryBannerReq): Promise<IGetProductExceedRsp | {}> {
    try {
      return await this.trpcPost('OverdueDataRecoveryBanner', req, { headers: {
        'X-ProductID': getProductId(),
      } });
    } catch (e) {
      return {};
    }
  }

  /**
   * 绑定个人资源包到产品
   */
  public async bindingResourceToProduct(req: IBindingResourceToProductReq):
  Promise<IBindingResourceToProductRsp> {
    try {
      return await this.trpcPost('BindingResourceToProduct', req);
    } catch (e) {
      return {};
    }
  }

  /**
   * 解绑产品资源包
   */
  public async removeBindingResourceFromProduct(req: IBindingResourceToProductReq):
  Promise<IBindingResourceToProductRsp> {
    try {
      return await this.trpcPost('RemoveBindingResourceFromProduct', req);
    } catch (e) {
      return {};
    }
  }

  /**
   * 查询资源包按天使用量分产品
   * @param req
   */
  public async getResourceUsageDetailByProducts(req: IGetResourceUsageDetailByProductsReq):
  Promise<IGetResourceUsageDetailByProductsRsp> {
    try {
      return await this.trpcPost('GetResourceUsageDetailByProducts', req);
    } catch (e) {
      return {};
    }
  }

  /**
   * 通过资源包id查询绑定的产品
   * @param req
   */
  public async getProductsFromResource(req: IGetProductsFromResourceReq):
  Promise<IResourceProducts[]> {
    try {
      const data = await this.trpcPost<IGetProductsFromResourceRsp>('GetProductsFromResource', req);
      return Array.isArray(data?.productInfos) ? data?.productInfos : [];
    } catch (e) {
      // 测试数据
      return [];
    }
  }

  public async hasBindingAnnualResource(req: IHasBindingAnnualResourceReq):
  Promise<boolean> {
    try {
      const data = await this.trpcPost<IHasBindingAnnualResourceRsp>('HasBindingAnnualResource', req);
      return data?.hasBind ?? false;
    } catch (e) {
      return false;
    }
  }
  // 查询月活资源包套餐趋势按月使用量
  public async getMAUResourceUsage(req: IGetMAUResourceUsageReq):
  Promise<IGetMAUResourceUsageRsp | undefined> {
    try {
      return await this.trpcPost<IGetMAUResourceUsageRsp>('GetMAUResourceUsage', req);
    } catch (e) {
      return undefined;
    }
  }

  // 查询月活资源包套餐趋势按月使用量 --分产品
  public async getMAUResourceUsageByProducts(req: IGetMAUResourceUsageReq):
  Promise<IGetMAUResourceUsageRsp> {
    try {
      return await this.trpcPost<IGetMAUResourceUsageRsp>('GetMAUResourceUsageByProducts', req);
    } catch (e) {
      return {};
    }
  }

  // 查询积分套餐使用额度
  public async getCreditPackageDetail(req: IGetCreditPackageDetailReq):
  Promise<IGetCreditPackageDetailRsp> {
    try {
      return await this.trpcPost<IGetCreditPackageDetailRsp>('GetCreditPackageDetail', req);
    } catch (e) {
      return {};
    }
  }

  // 查询积分消耗趋势
  public async getCreditUsageTrend(req: IGetCreditUsageTrendReq):
  Promise<IGetCreditUsageTrendRsp> {
    try {
      return await this.trpcPost<IGetCreditUsageTrendRsp>('GetCreditUsageTrend', req);
    } catch (e) {
      return {};
    }
  }

  // 查询成员使用汇总
  public async getMemberUsageSummary(req: IGetMemberUsageSummaryReq):
  Promise<IGetMemberUsageSummaryRsp> {
    try {
      return await this.trpcPost<IGetMemberUsageSummaryRsp>('GetMemberUsageSummary', req);
    } catch (e) {
      return {};
    }
  }

  // 查询积分详细使用记录
  public async getCreditUsageDetail(req: IGetCreditUsageDetailReq):
  Promise<IGetCreditUsageDetailRsp> {
    try {
      return await this.trpcPost<IGetCreditUsageDetailRsp>('GetCreditUsageDetail', req);
    } catch (e) {
      return {};
    }
  }
}


export const resourcePackageService = new ResourcePackageService();
